import Button from "components/base/Button";
import { Col, Form, Row, Card, Badge } from "react-bootstrap";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useState } from "react";
import Dropzone from "components/base/Dropzone";
import imageIcon from "assets/img/icons/cloud-upload.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faBuilding, 
  faImage, 
  faUser, 
  faFileUpload,
  faCloudUpload,
  faLightbulb,
  faBolt,
  faSave,
  faSpinner,
  faRocket,
  faCog
} from "@fortawesome/free-solid-svg-icons";

type FormData = {
  brokerage_name: string;
  subscriber_photo: string;
  brokerage_logo: string;
  brokerage_attachments: any;
};

const OnboardingForm = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);

  const handleDropzoneFiles = (acceptedFiles: File[]) => {
    setAttachments((prevAttachments) => [...prevAttachments, ...acceptedFiles]);
  };

  const handleUpdateProfile = async (data: FormData) => {
    try {
      setLoading(true);
      // Handle form submission logic here
      toast.success("Company information updated successfully!");
    } catch (error: any) {
      toast.error("Failed to update company information");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-6">
      {/* AI-Enhanced Form Header */}
      <div 
        className="mb-5 p-4 rounded-4"
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <div className="d-flex align-items-center">
          <div 
            className="rounded-circle d-flex align-items-center justify-content-center me-3"
            style={{ 
              width: "48px", 
              height: "48px",
              background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
              boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)'
            }}
          >
            <FontAwesomeIcon 
              icon={faRocket} 
              className="text-white" 
              size="lg"
              style={{ filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' }}
            />
          </div>
          <div>
            <h4 style={{ 
              color: '#1f2937',
              textShadow: '0 0 10px rgba(0, 212, 255, 0.3)',
              fontSize: '1.5rem',
              fontWeight: 600,
              marginBottom: '4px'
            }}>
              Company Intelligence Setup
            </h4>
            <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
              AI-powered business profile configuration
            </p>
          </div>
        </div>
      </div>

      <Form onSubmit={handleSubmit(handleUpdateProfile)}>
        {/* Company Basic Information Section */}
        <Card 
          className="mb-4 border-0"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(0, 212, 255, 0.1) !important',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 212, 255, 0.1)'
          }}
        >
          <Card.Header 
            className="border-bottom-0 py-3"
            style={{ 
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              borderRadius: '16px 16px 0 0'
            }}
          >
            <div className="d-flex align-items-center">
              <FontAwesomeIcon 
                icon={faBuilding} 
                className="me-2" 
                style={{ color: '#10b981' }}
              />
              <h6 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
                Business Information
              </h6>
              <Badge 
                bg="transparent" 
                className="ms-2 fw-semibold"
                style={{
                  background: 'rgba(16, 185, 129, 0.1)',
                  color: '#10b981',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  fontSize: '10px',
                  borderRadius: '10px'
                }}
              >
                AI Enhanced
              </Badge>
            </div>
          </Card.Header>
          
          <Card.Body className="p-4">
            <Row className="g-4">
              <Col lg={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" />
                    Brokerage Name <span className="text-danger"> *</span>
                  </Form.Label>
                  <Form.Control
                    {...register("brokerage_name", {
                      required: "Brokerage name is required",
                      maxLength: { value: 200, message: "Maximum 200 characters allowed" }
                    })}
                    type="text"
                    placeholder="Enter your brokerage name"
                    isInvalid={!!errors.brokerage_name}
                    style={{
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(0, 212, 255, 0.2)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease'
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.brokerage_name?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Media Upload Section */}
        <Card 
          className="mb-4 border-0"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(0, 212, 255, 0.1) !important',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 212, 255, 0.1)'
          }}
        >
          <Card.Header 
            className="border-bottom-0 py-3"
            style={{ 
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              borderRadius: '16px 16px 0 0'
            }}
          >
            <div className="d-flex align-items-center">
              <FontAwesomeIcon 
                icon={faImage} 
                className="me-2" 
                style={{ color: '#8b5cf6' }}
              />
              <h6 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
                Visual Identity
              </h6>
              <Badge 
                bg="transparent" 
                className="ms-2 fw-semibold"
                style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  color: '#8b5cf6',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  fontSize: '10px',
                  borderRadius: '10px'
                }}
              >
                AI Optimized
              </Badge>
            </div>
          </Card.Header>
          
          <Card.Body className="p-4">
            <Row className="g-4">
              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faImage} className="me-2 text-primary" />
                    Brokerage Logo
                  </Form.Label>
                  <Form.Control
                    {...register("brokerage_logo")}
                    type="file"
                    accept="image/*"
                    style={{
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease'
                    }}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '12px' }}>
                    Upload your company logo (PNG, JPG)
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
                    Subscriber Photo
                  </Form.Label>
                  <Form.Control
                    {...register("subscriber_photo")}
                    type="file"
                    accept="image/*"
                    style={{
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease'
                    }}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '12px' }}>
                    Upload your profile photo
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Document Upload Section */}
        <Card 
          className="mb-4 border-0"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(0, 212, 255, 0.1) !important',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 212, 255, 0.1)'
          }}
        >
          <Card.Header 
            className="border-bottom-0 py-3"
            style={{ 
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
              borderRadius: '16px 16px 0 0'
            }}
          >
            <div className="d-flex align-items-center">
              <FontAwesomeIcon 
                icon={faCloudUpload} 
                className="me-2" 
                style={{ color: '#f59e0b' }}
              />
              <h6 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
                Business Documents
              </h6>
              <Badge 
                bg="transparent" 
                className="ms-2 fw-semibold"
                style={{
                  background: 'rgba(245, 158, 11, 0.1)',
                  color: '#f59e0b',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  fontSize: '10px',
                  borderRadius: '10px'
                }}
              >
                AI Processing
              </Badge>
            </div>
          </Card.Header>
          
          <Card.Body className="p-4">
            <Row className="g-4">
              <Col lg={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faCloudUpload} className="me-2 text-primary" />
                    Brokerage Attachments
                  </Form.Label>
                  <div 
                    className="p-4 rounded-3 text-center"
                    style={{
                      background: 'rgba(245, 158, 11, 0.05)',
                      border: '2px dashed rgba(245, 158, 11, 0.3)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <Dropzone
                      accept={{
                        "application/pdf": [".pdf"],
                        "application/msword": [".doc"],
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
                      }}
                      uploadIcon={imageIcon}
                      isMulti
                      onDrop={handleDropzoneFiles}
                    />
                  </div>
                  {attachments.length > 0 && (
                    <div className="mt-3">
                      <small className="text-muted">Selected files: {attachments.length}</small>
                      <div className="mt-2">
                        {attachments.map((file, index) => (
                          <Badge 
                            key={index}
                            bg="warning" 
                            className="me-2 mb-2"
                            style={{ fontSize: '11px' }}
                          >
                            {file.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <Form.Text className="text-muted d-block mt-2" style={{ fontSize: '12px' }}>
                    Upload business documents, licenses, or certifications (PDF, DOC, DOCX)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* AI-Enhanced Submit Button */}
        <div className="text-center pt-4">
          <Button
            type="submit"
            disabled={loading}
            className="px-5 py-3 fw-semibold"
            style={{
              background: loading 
                ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)' 
                : 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
              border: 'none',
              borderRadius: '20px',
              fontSize: '16px',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              boxShadow: loading 
                ? 'none' 
                : '0 10px 30px rgba(0, 212, 255, 0.3)',
              transition: 'all 0.3s ease',
              minWidth: '200px'
            }}
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="me-2" spin />
                Setting up...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Complete Setup
              </>
            )}
          </Button>
        </div>
      </Form>

      {/* AI Enhancement Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .form-control:focus {
            border-color: rgba(0, 212, 255, 0.5) !important;
            box-shadow: 0 0 0 0.2rem rgba(0, 212, 255, 0.15) !important;
            background: rgba(255, 255, 255, 0.95) !important;
          }
          
          .btn:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 15px 40px rgba(0, 212, 255, 0.4) !important;
          }
          
          .form-control:hover:not(:disabled) {
            border-color: rgba(0, 212, 255, 0.4) !important;
            background: rgba(255, 255, 255, 0.9) !important;
          }
          
          .dropzone-container:hover {
            background: rgba(245, 158, 11, 0.08) !important;
            border-color: rgba(245, 158, 11, 0.5) !important;
          }
        `
      }} />
    </div>
  );
};

export default OnboardingForm;
